// KnowledgePage — gestione Knowledge Base per il Comandante AI.
// Upload manuali PDF, lista documenti, test di ricerca semantica.
import React, { useEffect, useState, useRef } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Upload, FileText, Trash2, Loader2, BookOpen, Search, Sparkles, X, CheckCircle2 } from "lucide-react";
import EmptyState from "@/components/ui/EmptyState";
import LoadingBlock from "@/components/ui/LoadingBlock";
import { toast } from "sonner";

export default function KnowledgePage() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [testQuery, setTestQuery] = useState("");
  const [testResults, setTestResults] = useState(null);
  const [testing, setTesting] = useState(false);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/knowledge/docs");
      setDocs(r.data.items || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) return toast.error("Seleziona un file PDF");
    if (file.size > 30 * 1024 * 1024) return toast.error("File troppo grande (max 30 MB)");

    const fd = new FormData();
    fd.append("file", file);
    if (title) fd.append("title", title);
    if (description) fd.append("description", description);
    setUploading(true);
    try {
      const r = await api.post("/knowledge/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 5 * 60 * 1000,
      });
      toast.success(`Caricato: ${r.data.doc.title} (${r.data.chunks_indexed} passaggi indicizzati)`);
      setTitle(""); setDescription("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore upload");
    } finally { setUploading(false); }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Eliminare definitivamente "${doc.title}"?`)) return;
    try {
      await api.delete(`/knowledge/docs/${doc.id}`);
      toast.success("Eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const onTestSearch = async (e) => {
    e?.preventDefault();
    if (!testQuery.trim()) return;
    setTesting(true);
    try {
      const r = await api.post("/knowledge/search", { query: testQuery, top_k: 5 });
      setTestResults(r.data.results || []);
    } catch { toast.error("Errore ricerca"); }
    finally { setTesting(false); }
  };

  return (
    <AppLayout
      title="Knowledge Base"
      subtitle="Manuali per il Comandante AI"
      actions={
        <button onClick={() => fileInputRef.current?.click()}
                className="text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} 
                data-testid="kb-upload-trigger">
          <Upload className="w-4 h-4" /> Carica manuale
        </button>
      }
    >
      <div className="bg-gradient-to-r from-[#F5EFE3] via-white to-[#F5EFE3]/40 border border-[#B7955B]/40 rounded-[3px] px-4 py-3 mb-5 flex items-start gap-3">
        <BookOpen className="w-5 h-5 text-[#B7955B] shrink-0 mt-0.5" />
        <div>
          <div className="text-xs uppercase tracking-wider text-[#8a6e3d] font-semibold mb-0.5">Come funziona</div>
          <div className="text-sm text-slate-700 leading-relaxed">
            Carica i tuoi manuali interni (criteri commerciali, tabelle tassi, procedure).
            Il Comandante AI userà i contenuti pertinenti quando risponderai alle tue domande,
            così risponde secondo i <strong>tuoi</strong> criteri aziendali.
            I file sono processati <strong>una sola volta</strong>, poi zero costi aggiuntivi.
          </div>
        </div>
      </div>

      <form onSubmit={onUpload} className="bg-white border border-slate-200 rounded-[3px] p-4 mb-6" data-testid="kb-upload-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Titolo (opzionale)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
                   placeholder="es. Politiche commerciali 2026" data-testid="kb-title"
                   className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]/20" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">File PDF</label>
            <input type="file" accept=".pdf,application/pdf" ref={fileInputRef} data-testid="kb-file"
                   className="w-full mt-1 text-sm file:mr-3 file:py-2 file:px-3 file:rounded-[3px] file:border file:border-slate-300 file:bg-slate-50 file:text-slate-700" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-wider text-slate-500">Descrizione (opz.)</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)}
                 placeholder="Breve nota su quando usare questo manuale..."
                 data-testid="kb-description"
                 className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-[3px] focus:border-[#0A192F] focus:ring-1 focus:ring-[#0A192F]/20" />
        </div>
        <div className="flex justify-end mt-3">
          <button type="submit" disabled={uploading} data-testid="kb-upload-btn"
                  className="text-white text-sm px-5 py-2 rounded-[3px] flex items-center gap-2 disabled:opacity-60" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} >
            {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Elaborazione...</> : <><Upload className="w-4 h-4" /> Carica e indicizza</>}
          </button>
        </div>
      </form>

      <div className="bg-white border border-slate-200 rounded-[3px] overflow-hidden">
        <div className="border-b border-[#F0F2F5] px-4 py-3 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#B7955B]" />
            <h2 className="font-serif text-base text-slate-900">Manuali caricati ({docs.length})</h2>
          </div>
        </div>
        {loading ? (
          <div className="p-4"><LoadingBlock variant="list" count={4} /></div>
        ) : docs.length === 0 ? (
          <div className="p-6">
            <EmptyState
              variant="knowledge"
              title="Nessun manuale caricato"
              description="Carica il primo PDF di manualistica (Creditis, portali, procedure interne). Il Comandante AI userà questi documenti per rispondere in modo preciso."
              compact
            />
          </div>
        ) : (
          <div className="divide-y divide-slate-100 stagger stagger-fade" data-testid="kb-list">
            {docs.map((d) => (
              <div key={d.id} className="px-4 py-3 flex items-start gap-3 hover:bg-slate-50 transition"
                   data-testid={`kb-doc-${d.id}`}>
                <div className="w-10 h-10 rounded-[3px] bg-[#F5EFE3] text-[#8a6e3d] flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 truncate">{d.title}</div>
                  <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-0.5">
                    <span>{d.num_pages || 0} pagine</span>
                    <span>{(d.char_count || 0).toLocaleString("it-IT")} caratteri</span>
                    <span>{new Date(d.created_at).toLocaleDateString("it-IT")}</span>
                  </div>
                  {d.description && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{d.description}</div>}
                </div>
                <button onClick={() => onDelete(d)} data-testid={`kb-del-${d.id}`}
                        className="p-1.5 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-[3px]">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Test di ricerca */}
      <form onSubmit={onTestSearch} className="mt-6 bg-white border border-slate-200 rounded-[3px] p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-[#B7955B]" />
          <h3 className="font-serif text-base text-slate-900">Test ricerca nei manuali</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Prova una domanda: vedrai quali passaggi dei manuali vengono richiamati dal Comandante AI.
        </p>
        <div className="flex gap-2">
          <div className="flex-1 flex items-center bg-white border border-slate-300 rounded-[3px] px-3 py-2 focus-within:border-[#0A192F]">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={testQuery} onChange={(e) => setTestQuery(e.target.value)}
                   placeholder="es. Quale tasso applichiamo per CQS sopra 60 anni?"
                   data-testid="kb-test-query"
                   className="flex-1 px-2 outline-none text-sm bg-transparent" />
          </div>
          <button type="submit" disabled={testing || !testQuery.trim()}
                  data-testid="kb-test-btn"
                  className="bg-[#B7955B] hover:bg-[#a0824a] text-white text-sm px-4 py-2 rounded-[3px] flex items-center gap-1.5 disabled:opacity-60">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cerca"}
          </button>
        </div>
        {testResults !== null && (
          <div className="mt-3 space-y-2" data-testid="kb-test-results">
            {testResults.length === 0 ? (
              <div className="text-sm text-slate-500 italic py-2">Nessun passaggio pertinente trovato.</div>
            ) : (
              testResults.map((r, i) => (
                <div key={i} className="bg-[#F5EFE3]/40 border border-[#E5D7B6] rounded-[3px] p-3">
                  <div className="flex items-center gap-2 mb-1.5 text-[10px] uppercase tracking-wider">
                    <CheckCircle2 className="w-3 h-3 text-[#8a6e3d]" />
                    <span className="text-[#8a6e3d] font-semibold">{r.docTitle}</span>
                    <span className="text-slate-500">· match {r.score}</span>
                  </div>
                  <div className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap">{r.chunkText}</div>
                </div>
              ))
            )}
          </div>
        )}
      </form>
    </AppLayout>
  );
}
